import { ForwardedRef, forwardRef, useContext, useState } from 'react'
import { MediaType } from 'types'
import { identifyMedia, useAsyncEffect } from 'utils'
import { MediaSprite } from '../../../types'
import { mediaSpriteFromSource } from '../../../utils/media/sprite'
import { ArtboardContext } from '../board/context'
import Layer, { LayerProps } from '../layer'
import ArtboardError from '../types/error'
import { ArtboardLayerRef } from '../types/refs'
import useImagePosition from './hooks/use-image-position'

const SUPPORTED_MEDIA: MediaType[] = ['gif', 'image']

interface Props extends LayerProps {
    source: File | string
    onError?: (error: ArtboardError) => void
}

const ArtboardImage = (
    { defaultPosition: positionProp, source, onError, ...layerProps }: Props,
    ref: ForwardedRef<ArtboardLayerRef>
) => {
    const { fps, width } = useContext(ArtboardContext)
    const [sprite, setSprite] = useState<MediaSprite | undefined>()
    const defaultPosition = useImagePosition(sprite, positionProp)

    useAsyncEffect(async () => {
        const mediaType = identifyMedia(source)

        // unsupported file
        if (!mediaType || SUPPORTED_MEDIA.indexOf(mediaType) < 0) {
            onError?.(ArtboardError.UnsupportedFile)
        }

        try {
            const sprite = await mediaSpriteFromSource(source, width, fps)

            setSprite(sprite)
        } catch (err) {
            onError?.(ArtboardError.UnableToLoadFile)
        }
    }, [fps, source])

    return defaultPosition && sprite ? (
        <Layer {...layerProps} sprite={sprite} defaultPosition={defaultPosition} ref={ref} />
    ) : null
}

export default forwardRef(ArtboardImage)
